import valid from "card-validator"
import isEmail from "validator/lib/isEmail"
import * as Yup from "yup"

import { jewlr_t } from "helpers/application"
import { creditCardCheckoutMethod } from "helpers/checkout/checkout_options"

const yup = (orderData, ccCheckoutMethod) => {
  ccCheckoutMethod ||= creditCardCheckoutMethod()

  return Yup.object().shape({
    account_create_error: Yup.mixed().test(
      "account_create_error",
      ({ value }) => value,
      (value) => [null, "", undefined].includes(value)
    ),
    bill_full_name: Yup.string().when(
      ["payment_method", "order_balance"],
      (payment_method, order_balance, schema) => {
        if (payment_method == "affirm" || payment_method == "financing") {
          return schema
        } else {
          return schema
            .required("Please enter your full name")
            .matches(/\w+(\s+\w+)+/, {
              message: "Please enter your first and last name",
            })
        }
      }
    ),
    cc:
      ccCheckoutMethod == "braintreeCheckout" ||
      ccCheckoutMethod == "stripeCheckout"
        ? null
        : Yup.object().when(
            ["payment_method", "order_balance"],
            (payment_method, order_balance, schema) => {
              if (
                payment_method == "affirm" ||
                payment_method == "financing" ||
                payment_method == "klarna" ||
                order_balance == 0.0
              ) {
                return schema
              } else {
                return schema.shape({
                  cc_number: Yup.string()
                    .test(
                      "test-number",
                      "Please enter a valid credit card number",
                      (value) =>
                        valid.number(value).isValid ||
                        // this is the failure credit card number, needs to be hardcoded so it doesn't get checked by Luhn algorithm
                        (value || "").replace(/\s/g, "") == "4222222222222222"
                    )
                    // .matches(
                    //   /(^4\d{12}(\d{3})?$)|(^(5[1-5]\d{4}|677189)\d{10}$)|(^(34|37)\d{13}$)|(^(6011|65\d{2})\d{12}$)|^2(?:2(?:2[1-9]|[3-9]\d)|[3-6]\d\d|7(?:[01]\d|20))\d{12}$/,
                    //   { message: "Please enter a valid credit card number." }
                    // )
                    .required("Please enter a valid credit card number"),
                  // cc_expiry_month: Yup.string()
                  //   .matches(/^\d{1,2}/, {
                  //     message: "Please select the expiry month of your credit card."
                  //   })
                  //   .required("Please select the expiry month of your credit card."),
                  // cc_expiry_year: Yup.string()
                  //   .required("Please select the expiry year of your credit card.")
                  //   .when("cc_expiry_month", (expiry_month, schema) => {
                  //     if (expiry_month == "") {
                  //       return schema.test(
                  //         "test-expiry-year",
                  //         "Please select the expiry year of your credit card.",
                  //         value => valid.expirationYear(value).isValid
                  //       )
                  //     } else {
                  //       return schema.test(
                  //         "test-expiry-date",
                  //         "Please enter a valid expiration date",
                  //         value =>
                  //           valid.expirationDate(expiry_month + value).isValid
                  //       )
                  //     }
                  //   }),
                  cc_verification_value: Yup.string()
                    .matches(/^\d{3,4}$/, {
                      message: "Please enter the security code on your card",
                    })
                    .required("Please enter the security code on your card"),
                  expiry: Yup.string().test(
                    "test-expiry",
                    "Please enter a valid expiry",
                    (value) => valid.expirationDate(value).isValid
                  ),
                })
              }
            }
          ),
    order: Yup.object().shape({
      account_create_password: Yup.string()
        .nullable()
        .when(["create_account"], (create_account, schema) => {
          if (!create_account) {
            return schema
          } else {
            return schema
              .required("Please enter your password.")
              .min(8, "Password must contain minimum 8 characters")
          }
        }),
      bill_address_attributes: Yup.object().when(
        ["payment_method", "balance", "same_as_shipping_address_b"],
        (payment_method, order_balance, same_as_shipping_address_b, schema) => {
          if (
            payment_method == "affirm" ||
            payment_method == "financing" ||
            payment_method == "klarna" ||
            order_balance == 0.0 ||
            same_as_shipping_address_b
          ) {
            return schema
          } else {
            return schema.shape({
              address1: Yup.string().required(
                "Please enter your billing street address"
              ),
              address2: Yup.string().nullable().max(64, "Maximum reached"),
              city: Yup.string().required(
                `Please enter your billing ${jewlr_t("address.city")}.`
              ),
              country: Yup.string().required(
                `Please select your billing ${jewlr_t("address.country")}`
              ),
              postal_code: Yup.string()
                .trim()
                .when("country", (country, schema) => {
                  if (orderData.country_postal_codes[country]) {
                    return schema.matches(
                      new RegExp(
                        orderData.country_postal_codes[country].regex,
                        "i"
                      ),
                      orderData.country_postal_codes[country].error
                    )
                  } else {
                    return schema
                  }
                })
                .required(orderData.errors.postal_code),
              state: Yup.string().when("country", (country, schema) => {
                if (orderData.bill_states && orderData.bill_states.length > 0) {
                  return schema.required(
                    `Please select a billing ${jewlr_t(
                      "address.state"
                    ).toLowerCase()}`
                  )
                } else {
                  return schema
                }
              }),
            })
          }
        }
      ),
      customer_phone: Yup.string().when(
        ["payment_method"],
        (payment_method, schema) => {
          return schema
            .matches(/^[\d\-+(). ]{10,20}$/, {
              message:
                "Please enter a valid phone number, in case we need it for delivery",
            })
            .required(
              "Please enter a valid phone number, in case we need it for delivery"
            )
        }
      ),
      email: Yup.string().test(
        "test-email",
        "Please enter a valid email address",
        (value) => isEmail(value || "")
      ),
      ship_address_attributes: Yup.object().shape({
        address1: Yup.string().required(
          "Please enter address you would like your order shipped to"
        ),
        address2: Yup.string()
          .nullable()
          .max(64, "Please enter an optional apartment, suite, unit #"),
        city: Yup.string().required(
          `Please enter a ${jewlr_t("address.city")} name`
        ),
        country: Yup.string().required("Please select your country"),
        first_name: Yup.string().required(
          "Please enter first name of the recipient"
        ),
        label: Yup.string()
          .nullable()
          .max(64, "Please enter an optional address name"),
        last_name: Yup.string().required(
          "Please enter last name of the recipient"
        ),
        // phone: Yup.string()
        //   .matches(/^[\d\-\+\(). ]{10,20}$/, {
        //     message: "Please enter a valid phone number for the recipient."
        //   })
        //   .required("Please enter a valid phone number for the recipient."),
        postal_code: Yup.string()
          .trim()
          .test(
            "postal_code",
            "We are unable to deliver to Guernsey/Isle of Man/Jersey - please change the delivery address",
            (value) => {
              const regexp = new RegExp("(^GY)|(^IM)|(^JE)", "i")
              return !regexp.test(value)
            }
          )
          .matches(new RegExp(orderData.regex.postal_code), {
            message: orderData.errors.postal_code,
          })
          .required(orderData.errors.postal_code),
        state: orderData.has_states
          ? Yup.string().required(
              `Please select a ${jewlr_t("terms.shipping")} ${jewlr_t(
                "address.state"
              )}`
            )
          : null,
      }),
    }),
    ship_full_name: Yup.string()
      .required("Please enter full name")
      .matches(/\w+(\s+\w+)+/, {
        message: "Please enter first and last name",
      }),
    ship_method_id: Yup.string().required(
      `Please select a ${jewlr_t("terms.shipping")} method`
    ),
    zip_mismatch_error: Yup.mixed().test(
      "zip_mismatch_error",
      "The zip code you entered does not match the state. Please verify.",
      (value) => {
        return [false, null, "", undefined].includes(value)
      }
    ),
  })
}

export default yup
